import { useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";

const MAX_REASON_LENGTH = 200;

type Props = {
  open: boolean;
  transactionId: string;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (waiveReason: string) => Promise<unknown> | unknown;
};

const getReasonError = (reason: string): string => {
  const trimmedReason = reason.trim();

  if (!trimmedReason) {
    return "Reason is required.";
  }

  if (trimmedReason.length > MAX_REASON_LENGTH) {
    return "Reason must be 200 characters or fewer.";
  }

  return "";
};

const ModalWaiveTransaction = ({
  open,
  transactionId,
  isSubmitting,
  onClose,
  onSubmit,
}: Props) => {
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const isLocallySubmittingRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setReason("");
      setReasonError("");
    }
  }, [open]);

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }

    setReason("");
    setReasonError("");
    onClose();
  };

  const handleSubmit = async () => {
    if (isSubmitting || isLocallySubmittingRef.current) {
      return;
    }

    const validationError = getReasonError(reason);

    if (validationError) {
      setReasonError(validationError);
      return;
    }

    try {
      isLocallySubmittingRef.current = true;
      await onSubmit(reason.trim());
    } catch (_error) {
      // Keep the modal open and preserve the reason so the user can retry.
    } finally {
      isLocallySubmittingRef.current = false;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle color="warning.main">Waive Transaction</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Waive reason
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="Please enter the reason for waiving this transaction"
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
            if (reasonError) {
              setReasonError("");
            }
          }}
          variant="outlined"
          required
          disabled={isSubmitting}
          error={Boolean(reasonError)}
          helperText={reasonError || `${reason.trim().length}/${MAX_REASON_LENGTH}`}
          inputProps={{ "aria-label": "Waive reason" }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="warning"
          variant="contained"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Waiving transaction" : "Waive transaction"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalWaiveTransaction;
